// src/pages/pengamatan/buah-alami/PlotDetailTooltip.jsx
import React from 'react';
import {
    Tooltip,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        maxWidth: 400,
        maxHeight: 400,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[3],
        padding: 0,
        overflow: 'hidden',
    },
}));

const PlotDetailTooltip = ({ order, children, currentTab }) => {
    if (!order || !order.allPlots || order.allPlots.length === 0) {
        return children;
    }
    
    // Hitung total plot (semua key plot yang ada di node plot)
    const totalPlotCount = order.allPlots.length;

    // Fungsi untuk mengurutkan plot secara ascending
    const sortPlotsAscending = (plots) => {
        return plots.sort((a, b) => {
            const plotA = a.plot || '';
            const plotB = b.plot || '';
            
            // Jika kedua plot berupa angka, urutkan secara numerik
            if (!isNaN(plotA) && !isNaN(plotB)) {
                return parseInt(plotA) - parseInt(plotB);
            }
            
            // Jika tidak, urutkan secara string
            return plotA.localeCompare(plotB);
        });
    };

    // Hitung plot dengan status sesuai tab aktif
    const getActivePlotCount = () => {
        let count = 0;
        
        switch (currentTab) {
            case 'Pending':
                count = order.allPlots.filter(plot => plot.status_pengamatan === 'Pending').length;
                break;
            case 'Approved':
                count = order.allPlots.filter(plot => plot.status_pengamatan === 'Approved').length;
                break;
            case 'Processed':
                count = order.allPlots.filter(plot => plot.status_pengamatan === 'Processed').length;
                break;
            case 'Closing':
                count = order.allPlots.length;
                break;
            default:
                count = order.allPlots.length;
        }
        
        return count;
    };

    const activePlotCount = getActivePlotCount();

    // Filter plots untuk ditampilkan di tabel
    const getFilteredPlots = () => {
        let filtered = [];
        
        switch (currentTab) {
            case 'Pending':
                filtered = order.allPlots.filter(plot => plot.status_pengamatan === 'Pending');
                break;
            case 'Approved':
                filtered = order.allPlots.filter(plot => plot.status_pengamatan === 'Approved');
                break;
            case 'Processed':
                filtered = order.allPlots.filter(plot => plot.status_pengamatan === 'Processed');
                break;
            case 'Closing':
                filtered = order.allPlots;
                break;
            default:
                filtered = order.allPlots;
        }
        
        // Urutkan plot secara ascending sebelum dikembalikan
        filtered = sortPlotsAscending(filtered);
        
        return filtered;
    };

    const filteredPlots = getFilteredPlots();

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending':
                return 'warning';
            case 'Approved':
                return 'success';
            case 'Processed':
                return 'info';
            default:
                return 'default';
        }
    };

    const tooltipContent = (
        <Box sx={{ p: 2, maxWidth: 400 }}>
            {/* Header */}
            <Typography variant="h6" component="div" gutterBottom sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                Detail Plot - {order.lokasi}
            </Typography>

            {/* Table dengan scrollable area */}
            {filteredPlots.length > 0 ? (
                <TableContainer 
                    component={Paper} 
                    variant="outlined" 
                    sx={{ 
                        mt: 1,
                        maxHeight: 200,
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#a8a8a8',
                        }
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    py: 1,
                                    backgroundColor: 'background.paper',
                                    width: '80px'
                                }}>
                                    Plot
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    py: 1,
                                    backgroundColor: 'background.paper',
                                    width: '100px'
                                }}>
                                    Luas Plot
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    py: 1,
                                    backgroundColor: 'background.paper'
                                }}>
                                    PJP
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPlots.map((plot, index) => (
                                <TableRow 
                                    key={index}
                                    sx={{ 
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        },
                                        // Beri background berbeda untuk setiap baris ganjil
                                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.02)'
                                    }}
                                >
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Typography variant="body2" fontWeight="medium">
                                            {plot.plot || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Typography variant="body2">
                                            {plot.luas_plot ? `${plot.luas_plot} ha` : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Typography variant="body2">
                                            {plot.pjp || '-'}
                                        </Typography>
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Tidak ada plot dengan status {currentTab}
                </Typography>
            )}

            {/* Subtitle - SEDERHANA */}
            <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                sx={{ 
                mt: 1, 
                pt: 1, 
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                textAlign: 'left'
            }}>
                Total: {activePlotCount}/{totalPlotCount} plot
            </Typography>
        </Box>
    );

    return (
        <StyledTooltip
            title={tooltipContent}
            placement="left"
            arrow
            enterDelay={500}
            leaveDelay={200}
        >
            <Box component="span">
                {children}
            </Box>
        </StyledTooltip>
    );
};

export default PlotDetailTooltip;